import { inject, Injectable, InjectionToken } from '@angular/core';
import { buildResponsesRequest, ChatEvent, toChatEvent } from './responses-protocol';
import { readServerSentEvents } from './sse';

/** Resolves the signed-in user's access token for the Bannerflow Resource API; empty when there is none. */
export const ACCESS_TOKEN = new InjectionToken<() => Promise<string>>('ACCESS_TOKEN');

export const FETCH = new InjectionToken<typeof fetch>('FETCH', {
    providedIn: 'root',
    factory: (): typeof fetch => globalThis.fetch.bind(globalThis)
});

export interface AgentClientConfig {
    /** agent-bff's base URL, without a trailing slash. */
    agentBffUrl: string;
    /** The agent's name in agent-bff's catalog. */
    agentName: string;
}

export const AGENT_CLIENT_CONFIG = new InjectionToken<AgentClientConfig>('AGENT_CLIENT_CONFIG');

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

/**
 * Sends one turn to the agent through agent-bff and yields the answer as it streams. Every
 * failure surfaces as an `error` event with a sentence a person can act on; nothing throws.
 */
@Injectable({ providedIn: 'root' })
export class AgentClient {
    private readonly fetch = inject(FETCH);
    private readonly accessToken = inject(ACCESS_TOKEN);
    private readonly config = inject(AGENT_CLIENT_CONFIG);

    async *send(input: string, previousResponseId: string | undefined): AsyncGenerator<ChatEvent> {
        let token: string;
        try {
            token = await this.accessToken();
        } catch (error) {
            yield { type: 'error', message: `Could not get an access token: ${messageOf(error)}` };
            return;
        }

        const headers = new Headers({
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
        });
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        let response: Response;
        try {
            response = await this.fetch(
                `${this.config.agentBffUrl}/agents/${this.config.agentName}/responses`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(buildResponsesRequest(input, previousResponseId))
                }
            );
        } catch {
            yield {
                type: 'error',
                message: `Could not reach agent-bff at ${this.config.agentBffUrl}. Check the network and that the origin is allowed.`
            };
            return;
        }

        if (!response.ok) {
            yield { type: 'error', message: await this.describeStatus(response) };
            return;
        }
        if (!response.body) {
            yield { type: 'error', message: 'agent-bff answered without a body' };
            return;
        }

        for await (const event of readServerSentEvents(response.body)) {
            const chatEvent = toChatEvent(event);
            if (chatEvent) {
                yield chatEvent;
            }
        }
    }

    private async describeStatus(response: Response): Promise<string> {
        switch (response.status) {
            case 401:
                return 'agent-bff rejected your sign-in (401). Sign out and in again.';
            case 403:
                return 'agent-bff does not accept your token as a user token (403).';
            case 404:
                return `agent-bff has no agent named "${this.config.agentName}" (404). The name must be in its catalog.`;
            default: {
                const body = (await response.text().catch(() => '')).trim();
                return body
                    ? `agent-bff answered ${response.status}: ${body}`
                    : `agent-bff answered ${response.status}`;
            }
        }
    }
}
